/**
 * Componente de tabela para distribuições da unidade
 * 
 * Responsável por:
 * - Renderizar dados das distribuições em formato de tabela
 * - Agrupar distribuições por concurso e produto
 * - Exibir informações de loteria, concurso e cotas
 * - Mostrar quantidade total disponível para retirada
 * 
 * @module App/UnityDistributions/Table
 */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Mask } from "@/utils/mask";
import { DistributionDataProps } from "@/pages/UnityDistributions";
import { useMemo } from "react";
import { WithdrawModal } from "../WithdrawModal";

interface TableDataProps {
  dataApi: DistributionDataProps[];
  canTakeBalance: boolean;
  onWithdrawSuccess: () => void;
}

interface GroupedDistribution {
  contest_id: number;
  contest: DistributionDataProps['contest'];
  product_classification_id: number;
  product_classification: DistributionDataProps['contest_product_classification'];
  lottery: DistributionDataProps['lottery'];
  unity: DistributionDataProps['unity'];
  created_by: DistributionDataProps['created_by'];
  total_quota_quantity: number;
  total_unity_balance: number;
  distributions: DistributionDataProps[];
}

export function TableData({ dataApi, canTakeBalance, onWithdrawSuccess }: TableDataProps) {
  // Pega o ID do vendedor do usuário logado
  const getSellerId = () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    return userData?.id || 1; // Valor padrão para teste
  };

  // Agrupa as distribuições por concurso e produto
  const groupedDistributions = useMemo(() => {
    const groups: { [key: string]: GroupedDistribution } = {};

    dataApi.forEach((distribution) => {
      const key = `${distribution.contest_id}-${distribution.contest_product_classification.product_classification_id}`;
      
      if (!groups[key]) {
        groups[key] = {
          contest_id: distribution.contest_id,
          contest: distribution.contest,
          product_classification_id: distribution.contest_product_classification.product_classification_id,
          product_classification: distribution.contest_product_classification,
          lottery: distribution.lottery,
          unity: distribution.unity,
          created_by: distribution.created_by,
          total_quota_quantity: 0,
          total_unity_balance: distribution?.cp_unity_balance?.unity_balance || 0,
          distributions: [],
        };
      }

      groups[key].total_quota_quantity += distribution.quota_quantity;
      groups[key].distributions.push(distribution);
    });

    return Object.values(groups);
  }, [dataApi]);

  return (
    <div className="rounded-md border">
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Loteria</TableHead>
            <TableHead>Concurso</TableHead>
            <TableHead>Produto</TableHead>
            <TableHead>Quantidade Total Distribuída</TableHead>
            <TableHead>Quantidade Disponível</TableHead>
            <TableHead>Prêmio</TableHead>
            <TableHead>Data de Validação</TableHead>
            <TableHead>Criado por</TableHead>
            <TableHead>Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groupedDistributions.length > 0 ? (
            groupedDistributions.map((group) => (
              <TableRow key={`${group.contest_id}-${group.product_classification_id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: group.lottery.color }}
                    />
                    <span className="font-medium">{group.lottery.name}</span>
                  </div>
                </TableCell>
                
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium">{group.contest.name}</span>
                    <span className="text-sm text-muted-foreground">
                      ID: {group.contest.id}
                    </span>
                  </div>
                </TableCell>
                
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {group.product_classification.product_name}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {group.product_classification.product_classification.name}
                    </span>
                  </div>
                </TableCell>
                
                <TableCell>
                  <Badge variant="secondary">
                    {group.total_quota_quantity} cotas
                  </Badge>
                </TableCell>
                
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <Badge 
                      variant={group.total_unity_balance > 0 ? "default" : "destructive"}
                    >
                      {group.total_unity_balance} cotas
                    </Badge>
                    {group.total_unity_balance > 0 && canTakeBalance && (
                      <span className="text-xs text-green-600 font-medium">
                        Disponível para retirada
                      </span>
                    )}
                  </div>
                </TableCell>
                
                <TableCell>
                  <span className="font-medium text-green-600">
                    {Mask.formatCurrency(group.contest.prize)}
                  </span>
                </TableCell>
                
                <TableCell>
                  {Mask.transformMaskBirthdayInBr(
                    group.contest.validation_date
                  )}
                </TableCell>
                
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {group.created_by.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {group.distributions.length} distribuição(ões)
                    </span>
                  </div>
                </TableCell>
                
                <TableCell>
                  <div className="flex gap-2">
                    {canTakeBalance && group.total_unity_balance > 0 ? (
                      <WithdrawModal
                        unity_id={group.unity.id}
                        contest_id={group.contest_id}
                        contest_product_classification_id={group.product_classification_id}
                        contest_name={group.contest.name}
                        product_name={`${group.product_classification.product_name} - ${group.product_classification.product_classification.name}`}
                        available_quantity={group.total_unity_balance}
                        seller_id={getSellerId()}
                        onSuccess={onWithdrawSuccess}
                      />
                    ) : (
                      <Badge variant="outline" className="text-gray-500">
                        {!canTakeBalance ? 'Bloqueado' : 'Sem saldo'}
                      </Badge>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={9} className="h-24 text-center">
                Nenhuma distribuição encontrada.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      
      {/* Resumo das distribuições */}
      {groupedDistributions.length > 0 && (
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <h4 className="font-medium mb-2">Resumo das Distribuições</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Total de Concursos: </span>
              <span className="font-medium">
                {new Set(groupedDistributions.map(g => g.contest_id)).size}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Total de Cotas Distribuídas: </span>
              <span className="font-medium">
                {groupedDistributions.reduce((sum, g) => sum + g.total_quota_quantity, 0)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Total Disponível para Retirada: </span>
              <span className="font-medium text-green-600">
                {groupedDistributions.reduce((sum, g) => sum + g.total_unity_balance, 0)} cotas
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 